import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { t } from '../../utils/creatorLang';
import { useCreatorAuth } from '../../utils/useCreatorAuth';
import {
  CreatorScreen, Card, Pill, BottomNav, ScreenHeader,
  I, SectionLabel, DiamondGem, TierBadge,
} from '../../components/creator/primitives';

// Style tags for example_text
const TAG_STYLES = {
  t: { color: 'var(--accent-pink)', fontWeight: 700 },
  g: { color: 'var(--accent-green)', fontWeight: 600 },
  w: { color: 'var(--fg-0)', fontWeight: 600 },
  big: { color: 'var(--accent-green)', fontWeight: 700, fontSize: 14 },
};
function renderStyledText(text) {
  const regex = /<(t|g|w|big)>(.*?)<\/\1>/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(<span key={match.index} style={TAG_STYLES[match[1]]}>{match[2]}</span>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

const EVOLUTION_TIERS = [
  { pct: 4, days: 11, hours: 30 },
  { pct: 5, days: 15, hours: 40 },
  { pct: 6, days: 18, hours: 60 },
  { pct: 7, days: 22, hours: 80 },
];
const MAINTIEN_TIERS = [
  { pct: 2.5, days: 11, hours: 30 },
  { pct: 3.5, days: 15, hours: 40 },
  { pct: 4, days: 18, hours: 60 },
  { pct: 5, days: 22, hours: 80 },
];

const fmtD = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'k';
  return String(Math.round(n));
};

const getCurrentTierIndex = (tiers, liveDays, liveHours) => {
  let idx = -1;
  for (let i = 0; i < tiers.length; i++) {
    if (liveDays >= tiers[i].days && liveHours >= tiers[i].hours) idx = i;
  }
  return idx;
};

const TierTable = ({ title, subtitle, tiers, accentVar, highlightIndex }) => (
  <Card pad={16} style={{ marginBottom: 12 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-0)' }}>{title}</span>
      <Pill color={accentVar === 'accent-green' ? 'green' : 'cyan'} style={{ fontSize: 10, padding: '2px 8px' }}>
        max {tiers[tiers.length - 1].pct}%
      </Pill>
    </div>
    <div style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 14 }}>{subtitle}</div>

    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1,
      background: 'var(--line-1)', borderRadius: 10, overflow: 'hidden',
    }}>
      {[t('days_label'), t('hours'), t('cashback'), 'Ex. 1M'].map((h, i) => (
        <div key={i} style={{ background: 'var(--bg-3)', padding: '6px 4px', textAlign: 'center' }}>
          <span style={{ fontSize: 9, color: 'var(--fg-3)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.3 }}>{h}</span>
        </div>
      ))}
      {tiers.map((tier, i) => {
        const isHi = highlightIndex === i;
        const rowBg = isHi ? 'rgba(255,255,255,0.04)' : 'var(--bg-2)';
        const borderLeft = isHi ? `2px solid var(--${accentVar})` : 'none';
        return (
          <React.Fragment key={i}>
            <div style={{ background: rowBg, padding: '10px 4px', textAlign: 'center', borderLeft }}>
              <span className="mono" style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-0)' }}>{tier.days}d</span>
            </div>
            <div style={{ background: rowBg, padding: '10px 4px', textAlign: 'center' }}>
              <span className="mono" style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-0)' }}>{tier.hours}h</span>
            </div>
            <div style={{ background: rowBg, padding: '10px 4px', textAlign: 'center' }}>
              <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: `var(--${accentVar})` }}>{tier.pct}%</span>
            </div>
            <div style={{ background: rowBg, padding: '10px 4px', textAlign: 'center' }}>
              <span className="mono" style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-2)' }}>
                {Math.round(1_000_000 * tier.pct / 100).toLocaleString('en-US')}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  </Card>
);

const SituationCard = ({ monthData }) => {
  const tierCurrent = monthData?.tier_current || 0;
  const tierM1 = monthData?.tier_m1 || 0;
  const diamonds = monthData?.diamonds || 0;
  const liveDays = monthData?.live_days || 0;
  const liveHours = parseFloat(((monthData?.live_duration_minutes || 0) / 60).toFixed(1));

  let situation, color, label, desc;
  if (tierCurrent === 1 && tierM1 > 1) {
    situation = 'early_month'; color = 'cyan'; label = t('situation_early_month'); desc = t('situation_early_month_desc');
  } else if (tierCurrent > tierM1 && tierM1 > 0) {
    situation = 'evolution'; color = 'green'; label = t('situation_evolution'); desc = t('situation_evolution_desc');
  } else if (tierCurrent === tierM1 && tierCurrent > 0 && diamonds >= 200_000) {
    situation = 'maintenance'; color = 'cyan'; label = t('situation_maintenance'); desc = t('situation_maintenance_desc');
  } else {
    situation = 'not_eligible'; color = 'gold'; label = t('situation_not_eligible'); desc = t('situation_not_eligible_desc');
  }

  const activeTiers = situation === 'evolution' ? EVOLUTION_TIERS : situation === 'maintenance' ? MAINTIEN_TIERS : null;
  const currentIdx = activeTiers ? getCurrentTierIndex(activeTiers, liveDays, liveHours) : -1;
  const currentPct = currentIdx >= 0 ? activeTiers[currentIdx].pct : 0;
  const nextTier = activeTiers && currentIdx + 1 < activeTiers.length ? activeTiers[currentIdx + 1] : null;

  const tierDisplay = tierCurrent > tierM1 && tierM1 > 0 ? `T${tierM1} → T${tierCurrent}`
    : tierCurrent === tierM1 ? `T${tierCurrent} = T${tierM1}`
    : `T${tierCurrent}`;

  const borderColorMap = { green: 'var(--accent-green)', cyan: 'var(--accent-cyan)', gold: 'var(--accent-gold)' };

  return (
    <Card pad={16} style={{ borderLeft: `3px solid ${borderColorMap[color]}`, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <SectionLabel>{t('your_situation')}</SectionLabel>
        <Pill color={color} style={{ padding: '2px 10px', fontSize: 10, letterSpacing: 0.3 }}>{label}</Pill>
      </div>

      <div style={{ fontSize: 12, color: 'var(--fg-2)', lineHeight: 1.5, marginBottom: 14 }}>{desc}</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
        {[
          ['Tier', tierDisplay],
          [t('diamonds'), fmtD(diamonds)],
          [t('live_days'), liveDays],
          [t('hours'), `${liveHours}h`],
        ].map(([lab, val], i) => (
          <div key={i} style={{
            background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 10,
          }}>
            <div style={{ fontSize: 9, color: 'var(--fg-3)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 4, letterSpacing: 0.3 }}>{lab}</div>
            <div className="mono tab" style={{ fontSize: 16, fontWeight: 700, color: 'var(--fg-0)' }}>{val}</div>
          </div>
        ))}
      </div>

      {activeTiers && (
        <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 12 }}>
          {currentPct > 0 ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: nextTier ? 8 : 0 }}>
                <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{t('current_cashback_tier')}</span>
                <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: borderColorMap[color] }}>{currentPct}%</span>
              </div>
              {nextTier ? (
                <div style={{ fontSize: 11, color: 'var(--fg-3)', lineHeight: 1.5 }}>
                  <span style={{ color: 'var(--fg-2)', fontWeight: 600 }}>{t('next_tier_label')}: {nextTier.pct}%</span>
                  {' — '}
                  {t('need_more')} <span style={{ color: 'var(--fg-0)', fontWeight: 600 }}>{Math.max(0, nextTier.days - liveDays)}</span> {t('live_days_short')} {t('and_word')} <span style={{ color: 'var(--fg-0)', fontWeight: 600 }}>{Math.max(0, Math.ceil(nextTier.hours - liveHours))}</span> {t('live_hours_short')}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: borderColorMap[color], fontWeight: 600 }}>
                  {t('max_tier_reached')}
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{t('current_cashback_tier')}</span>
                <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-4)' }}>0%</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--fg-3)', lineHeight: 1.5 }}>
                <span style={{ color: 'var(--fg-2)', fontWeight: 600 }}>{t('next_tier_label')}: {activeTiers[0].pct}%</span>
                {' — '}
                {t('need_more')} <span style={{ color: 'var(--fg-0)', fontWeight: 600 }}>{Math.max(0, activeTiers[0].days - liveDays)}</span> {t('live_days_short')} {t('and_word')} <span style={{ color: 'var(--fg-0)', fontWeight: 600 }}>{Math.max(0, Math.ceil(activeTiers[0].hours - liveHours))}</span> {t('live_hours_short')}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
};

const CreatorRules = () => {
  const navigate = useNavigate();
  const { tiktokId: authTiktokId, loading: authLoading, isDebugMode } = useCreatorAuth();
  const qs = isDebugMode && authTiktokId ? `?creator=${authTiktokId}` : '';

  const [monthData, setMonthData] = useState(null);

  useEffect(() => {
    if (authLoading || !authTiktokId) return;
    const fetchData = async () => {
      try {
        const { data } = await supabase
          .from('black_creator_current_month')
          .select('diamonds, tier_current, tier_m1, live_days, live_duration_minutes, is_active')
          .eq('tiktok_id', authTiktokId)
          .single();
        if (data) setMonthData(data);
      } catch (err) {
        console.error('Rules fetch error:', err);
      }
    };
    fetchData();
  }, [authTiktokId, authLoading]);

  const { evoHighlight, mainHighlight } = useMemo(() => {
    if (!monthData) return { evoHighlight: -1, mainHighlight: -1 };
    const tierCurrent = monthData.tier_current || 0;
    const tierM1 = monthData.tier_m1 || 0;
    const diamonds = monthData.diamonds || 0;
    const liveDays = monthData.live_days || 0;
    const liveHours = (monthData.live_duration_minutes || 0) / 60;

    let sit;
    if (tierCurrent === 1 && tierM1 > 1) sit = 'early_month';
    else if (tierCurrent > tierM1 && tierM1 > 0) sit = 'evolution';
    else if (tierCurrent === tierM1 && tierCurrent > 0 && diamonds >= 200_000) sit = 'maintenance';
    else sit = 'not_eligible';

    return {
      evoHighlight: sit === 'evolution' ? getCurrentTierIndex(EVOLUTION_TIERS, liveDays, liveHours) : -1,
      mainHighlight: sit === 'maintenance' ? getCurrentTierIndex(MAINTIEN_TIERS, liveDays, liveHours) : -1,
    };
  }, [monthData]);

  return (
    <CreatorScreen>
      <div className="screen grain">
        <ScreenHeader
          title={t('cashback_rules')}
          subtitle="NoiRock Program"
          onBack={() => navigate(`/my/dashboard${qs}`)}
        />

        {/* Intro */}
        <div style={{ padding: '0 20px 12px' }}>
          <Card pad={16}>
            <div style={{ fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.6 }}>
              {t('rules_intro')}
            </div>
          </Card>
        </div>

        {/* Your situation */}
        {monthData && (
          <div style={{ padding: '0 20px' }}>
            <SituationCard monthData={monthData}/>
          </div>
        )}

        {/* How it works */}
        <div style={{ padding: '0 20px 12px' }}>
          <SectionLabel>{t('how_it_works')}</SectionLabel>
          <Card pad={16}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { n: 1, title: t('tier_evolution'), desc: t('tier_evolution_desc'), color: 'var(--accent-green)', soft: 'var(--accent-green-soft)' },
                { n: 2, title: t('tier_maintenance'), desc: t('tier_maintenance_desc'), color: 'var(--accent-cyan)', soft: 'var(--accent-cyan-soft)' },
                { n: 3, title: t('activity_level'), desc: t('activity_level_desc'), color: 'var(--accent-gold)', soft: 'var(--accent-gold-soft)' },
              ].map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: 12 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: step.soft,
                    border: `1px solid ${step.color}`,
                    display: 'grid', placeItems: 'center', flexShrink: 0,
                    fontSize: 12, fontWeight: 700, color: step.color,
                    fontFamily: 'var(--font-mono)',
                  }}>{step.n}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-0)', marginBottom: 2 }}>{step.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--fg-3)', lineHeight: 1.5 }}>{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Tables */}
        <div style={{ padding: '0 20px' }}>
          <TierTable
            title={t('evolution_bonus')}
            subtitle={t('evolution_subtitle')}
            tiers={EVOLUTION_TIERS}
            accentVar="accent-green"
            highlightIndex={evoHighlight}
          />
          <TierTable
            title={t('maintenance_bonus')}
            subtitle={t('maintenance_subtitle')}
            tiers={MAINTIEN_TIERS}
            accentVar="accent-cyan"
            highlightIndex={mainHighlight}
          />
        </div>

        {/* Example */}
        <div style={{ padding: '0 20px 12px' }}>
          <SectionLabel>{t('example')}</SectionLabel>
          <Card pad={16} style={{
            background: 'var(--accent-green-soft)',
            border: '1px solid oklch(0.78 0.17 155 / 0.25)',
          }}>
            <div style={{ fontSize: 12, color: 'var(--fg-1)', lineHeight: 1.8 }}>
              {renderStyledText(t('example_text'))}
            </div>
          </Card>
        </div>

        {/* Tips */}
        <div style={{ padding: '0 20px 12px' }}>
          <SectionLabel>{t('tips')}</SectionLabel>
          <Card pad={16}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[t('tip_1'), t('tip_2'), t('tip_3'), t('tip_4')].map((tip, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--accent-pink)', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>→</span>
                  <span style={{ fontSize: 12, color: 'var(--fg-2)', lineHeight: 1.5 }}>{tip}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <BottomNav active="" qs={qs}/>
    </CreatorScreen>
  );
};

export default CreatorRules;
